import type { UserCardStage } from "@-/dev.improve.haniverse.constants.user-card-stage";
import type { DeckStudyCard } from "@-/dev.improve.haniverse.lib.cards";
import {
  getMinUserCardStage,
  getUserCardStage,
} from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import {
  getMorphemeGlyphTileStageFromUserCardStage,
  type MorphemeGlyphTileStage,
  type MorphemeStagesByGrapheme,
} from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { deckStudyCardSubject } from "#deck-study-card-subject";

/**
 * `front → SRS button stage` for a deck, derived from the deck's own actor rows.
 *
 * Same rule as the batched `useMorphemeListStagesQuery` read (weakest stage across the subject's
 * studied cards; a preserved row with neither an FSRS card nor manual mastery is not in the deck
 * and contributes nothing), but computed from rows the deck already holds — so the tile fills
 * paint on the first render without a second actor read or a change subscription. Feed it the
 * UNFILTERED deck rows: a grapheme's stage spans all of its cards, including ones the pinyin
 * flashcards setting or a toolbar filter hides from the list.
 *
 * Unlike the grapheme-keyed query, word subjects get a stage too — a studied word card is as
 * much a study object as a character card.
 */
export function deriveDeckStageByFront(
  studyCards: readonly DeckStudyCard[],
): MorphemeStagesByGrapheme {
  const stagesByFront = new Map<string, UserCardStage[]>();
  for (const studyCard of studyCards) {
    const subject = deckStudyCardSubject(studyCard);
    if (subject === undefined) {
      continue;
    }
    // Removing/clearing a card from the deck deliberately preserves its `user_card` and overlay so
    // user-authored mnemonics survive. Deck membership is the FSRS relation (or manual mastery).
    if (studyCard.fsrsCard === null && studyCard.manuallyMasteredAt === null) {
      continue;
    }
    const stages = stagesByFront.get(subject.text) ?? [];
    stages.push(getUserCardStage(studyCard));
    stagesByFront.set(subject.text, stages);
  }

  const stageByFront: Record<string, MorphemeGlyphTileStage> = {};
  for (const [front, stages] of stagesByFront) {
    stageByFront[front] = getMorphemeGlyphTileStageFromUserCardStage(
      getMinUserCardStage(stages),
    );
  }
  return stageByFront;
}
